import { Injectable, OnDestroy, WritableSignal, inject, signal } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription, interval } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';

import { AuthHTTPService } from './auth-http.service';
import { AuthModel, MfaChallengeModel } from '../../interfaces/auth/auth.model';
import { UserModel } from '../../interfaces/auth/user.model';

import { MenuItemsService } from '../../layout/service/menu-items.service';
import {
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from '../../interfaces/auth/forgot-password.model';
import { SignalRService } from '../signalr/signal-r.service';
import { Roles } from '../../utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private authHttpService = inject(AuthHTTPService);
  private signalRService = inject(SignalRService);
  private router = inject(Router);
  private menuItems = inject(MenuItemsService);

  private appVersion = 'v8.1.7';
  private userDataKey = 'authf649fc9a5f55';
  private authStorageKey = `${this.appVersion}-${this.userDataKey}-auth`;
  private currentUserStorageKey = `${this.appVersion}-${this.userDataKey}-user`;

  private unsubscribe: Subscription[] = [];
  private sessionInterval?: Subscription;

  currentUser$: Observable<any>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<any>;
  isLoadingSubject: BehaviorSubject<boolean>;

  isSessionExpired: WritableSignal<boolean> = signal(false);
  timeLeft: WritableSignal<number> = signal(0);
  currentUserSig: WritableSignal<any> = signal(undefined);

  get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: any) {
    this.currentUserSubject.next(user);
  }

  getUserRoleSig = () => this.currentUserSig()?.role ?? null;

  getUserRole(): Roles | null {
    return this.currentUserSubject.value?.role ?? null;
  }

  constructor() {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<any>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();

    this.monitorSession();
    this.initUser();

    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }

  private initUser(): void {
    const currentUser = this.getCurrentUserFromLocalStorage();
    if (currentUser) {
      this.currentUserSig.set(currentUser);
      this.currentUserSubject.next(currentUser);
      this.menuItems.setMenuItems(currentUser.role);
    }
  }

  private getCurrentUserFromLocalStorage(): any {
    const userJson = localStorage.getItem(this.currentUserStorageKey);
    return userJson ? JSON.parse(userJson) : undefined;
  }

  private setCurrentUserInLocalStorage(user: any): void {
    this.menuItems.setMenuItems(user.role);
    localStorage.setItem(this.currentUserStorageKey, JSON.stringify(user));
  }

  private clearCurrentUserFromLocalStorage(): void {
    localStorage.removeItem(this.currentUserStorageKey);
  }

  private getAuthFromLocalStorage(): AuthModel | undefined {
    try {
      const lsValue = localStorage.getItem(this.authStorageKey);
      return lsValue ? JSON.parse(lsValue) : undefined;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  private setAuthInLocalStorage(auth: AuthModel): void {
    localStorage.setItem(this.authStorageKey, JSON.stringify(auth));
  }

  private clearAuthFromLocalStorage(): void {
    localStorage.removeItem(this.authStorageKey);
  }

  private isExpired(auth: AuthModel): boolean {
    return Date.now() >= new Date(auth.expiresAtUtc).getTime();
  }

  login(email: string, password: string): Observable<MfaChallengeModel | null> {
    this.isLoadingSubject.next(true);

    return this.authHttpService.login(email, password).pipe(
      switchMap((response) => {
        // MFA skipped
        if ('token' in response && response.token) {
          return this.completeLogin(response as AuthModel);
        }
        // MFA step
        return of(response as MfaChallengeModel);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  verifyMfa(email: string, code: string): Observable<any> {
    this.isLoadingSubject.next(true);

    return this.authHttpService.verifyMfa({ email, code }).pipe(
      switchMap((auth) => this.completeLogin(auth)),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  refreshToken(): Observable<AuthModel | null> {
    const auth = this.getAuthFromLocalStorage();

    if (!auth?.token) {
      return of(null);
    }

    return this.authHttpService.refreshToken(auth.token).pipe(
      map((newAuth) => {
        const normalizedAuth = this.normalizeAuth(newAuth);
        this.setAuthInLocalStorage(normalizedAuth);
        this.signalRService.setToken(normalizedAuth.token);
        this.timeLeft.set(
          Math.max(new Date(normalizedAuth.expiresAtUtc).getTime() - Date.now(), 0)
        );
        this.isSessionExpired.set(false);
        return normalizedAuth;
      }),
      catchError(() => {
        this.logout();
        return of(null);
      })
    );
  }

  getValidToken(): Observable<string | null> {
    const auth = this.getAuthFromLocalStorage();

    if (!auth?.token) {
      return of(null);
    }

    if (this.isExpired(auth)) {
      this.logout();
      return of(null);
    }

    return of(auth.token);
  }

  logout(returnUrl?: string | null) {
    this.signalRService.setToken(null);
    this.signalRService.destroyConnection();
    this.clearAuthFromLocalStorage();
    this.clearCurrentUserFromLocalStorage();
    this.currentUserSubject.next(null);
    this.currentUserSig.set(null);
    this.timeLeft.set(0);
    this.isSessionExpired.set(false);
    this.sessionInterval?.unsubscribe();

    const currentUrl = returnUrl === null ? null : this.router.url;
    const allowedReturnUrl = this.getAllowedReturnUrl(returnUrl ?? currentUrl);

    this.router.navigate(['/auth/login'], {
      queryParams: allowedReturnUrl ? { returnUrl: allowedReturnUrl } : undefined,
    });
  }

  private getAllowedReturnUrl(returnUrl?: string | null): string | null {
    if (!returnUrl || typeof returnUrl !== 'string') {
      return null;
    }

    // Allow only absolute in-app paths to avoid open redirects.
    if (!returnUrl.startsWith('/') || returnUrl.startsWith('//') || returnUrl.includes('://')) {
      return null;
    }

    if (returnUrl.startsWith('/auth')) {
      return null;
    }

    return returnUrl;
  }

  isLoggedIn(): boolean {
    const auth = this.getAuthFromLocalStorage();
    return !!auth?.token;
  }

  getCurrentToken(): string | null {
    return this.getAuthFromLocalStorage()?.token ?? null;
  }

  getUserByToken(): Observable<any> {
    const auth = this.getAuthFromLocalStorage();

    if (!auth?.token) {
      return of(undefined);
    }

    if (this.isExpired(auth)) {
      this.logout();
      return of(undefined);
    }

    const auth$ = of(auth);

    return auth$.pipe(
      switchMap((validAuth) => {
        if (!validAuth?.token) {
          this.logout();
          return of(undefined);
        }

        return this.authHttpService.getCurrentUser(validAuth.token);
      }),
      map((user: any) => {
        if (user) {
          const latestAuth = this.getAuthFromLocalStorage();

          if (latestAuth?.token) {
            this.signalRService.setToken(latestAuth.token);
          }

          this.setCurrentUserInLocalStorage(user);
          this.currentUserSubject.next(user);
          this.currentUserSig.set(user);
        } else {
          this.logout();
        }

        return user;
      }),
      catchError(() => {
        this.logout();
        return of(undefined);
      })
    );
  }

  registration(user: UserModel): Observable<any> {
    this.isLoadingSubject.next(true);

    return this.authHttpService.createUser(user).pipe(
      map(() => undefined),
      switchMap(() => this.login(user.email, user.password)),
      catchError(() => of(undefined)),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  private completeLogin(auth: AuthModel): Observable<any> {
    const normalizedAuth = this.normalizeAuth(auth);
    this.setAuthInLocalStorage(normalizedAuth);
    this.signalRService.setToken(normalizedAuth.token);
    this.monitorSession();

    return this.getUserByToken().pipe(
      map((user) => {
        if (user) {
          this.signalRService.startConnection();
        }

        return user;
      })
    );
  }

  private normalizeAuth(auth: AuthModel): AuthModel {
    const rawAuth = auth as AuthModel & {
      Token?: string;
      ExpiresAtUtc?: string;
      RefreshAfterUtc?: string;
    };

    return {
      token: rawAuth.token ?? rawAuth.Token ?? '',
      email: rawAuth.email,
      expiresAtUtc: rawAuth.expiresAtUtc ?? rawAuth.ExpiresAtUtc ?? '',
      refreshAfterUtc: rawAuth.refreshAfterUtc ?? rawAuth.RefreshAfterUtc ?? '',
    };
  }

  forgotPassword(
    forgotPasswordModel: ForgotPasswordRequest
  ): Observable<ForgotPasswordRequest> {
    this.isLoadingSubject.next(true);

    return this.authHttpService
      .forgotPassword(forgotPasswordModel)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  resetPassword(
    resetPassword: ResetPasswordRequest
  ): Observable<ResetPasswordRequest> {
    this.isLoadingSubject.next(true);

    return this.authHttpService
      .resetPassword(resetPassword)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  private monitorSession(): void {
    this.sessionInterval?.unsubscribe();

    this.sessionInterval = interval(1000).subscribe(() => {
      const auth = this.getAuthFromLocalStorage();

      if (!auth?.token) {
        this.timeLeft.set(0);
        this.isSessionExpired.set(false);
        return;
      }

      const expiresAt = new Date(auth.expiresAtUtc).getTime();
      const now = Date.now();
      const remainingTime = expiresAt - now;

      this.timeLeft.set(Math.max(remainingTime, 0));
      this.isSessionExpired.set(remainingTime > 0 && remainingTime <= 180_000);

      if (remainingTime <= 0) {
        this.logout();
        return;
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
    this.sessionInterval?.unsubscribe();
  }
}
